import React, { useState } from 'react';
import { db } from '../config/firebase';
import { collection, doc, setDoc, getDocs, query } from 'firebase/firestore';

export const TestFirestorePage: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const addLog = (message: string) => {
    console.log(message);
    setLogs((prev) => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testWrite = async () => {
    try {
      setLoading(true);
      addLog('Starting Firestore write test...');

      const testRef = doc(db, 'users', 'test-user-' + Date.now());
      addLog(`Writing to: ${testRef.path}`);

      await setDoc(testRef, {
        email: 'test@example.com',
        name: 'Test User',
        timestamp: new Date().toISOString(),
      });

      addLog('✅ Write successful!');
      addLog('Document created at: users/' + testRef.id);
    } catch (error: any) {
      addLog(`❌ Write failed: ${error.message}`);
      addLog(`Error code: ${error.code}`);
    } finally {
      setLoading(false);
    }
  };

  const testRead = async () => {
    try {
      setLoading(true);
      addLog('Starting Firestore read test...');

      const q = query(collection(db, 'users'));
      addLog('Querying users collection...');

      const snapshot = await getDocs(q);
      addLog(`✅ Read successful! Found ${snapshot.size} documents`);

      snapshot.forEach((doc) => {
        addLog(`  - ${doc.id}: ${doc.data().email || 'no email'}`);
      });

      if (snapshot.size === 0) {
        addLog('No users found in Firestore');
      }
    } catch (error: any) {
      addLog(`❌ Read failed: ${error.message}`);
      addLog(`Error code: ${error.code}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace', maxWidth: '900px', margin: '0 auto' }}>
      <h1>🔥 Firestore Connection Test</h1>

      <div style={{ marginBottom: '20px', padding: '15px', background: '#f0f0f0', borderRadius: '8px' }}>
        <h3>Test Firestore Connectivity</h3>
        <p>This page tests if your app can write to and read from Firestore.</p>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <button
            onClick={testWrite}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: 'var(--primary-button)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Testing...' : 'Test Write'}
          </button>
          <button
            onClick={testRead}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Testing...' : 'Test Read'}
          </button>
        </div>
      </div>

      <div
        style={{
          background: '#1e1e1e',
          color: '#00ff00',
          padding: '15px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '12px',
          maxHeight: '500px',
          overflow: 'auto',
          lineHeight: '1.6',
        }}
      >
        {logs.length === 0 ? (
          <div style={{ color: '#888' }}>Click buttons above to run tests...</div>
        ) : (
          logs.map((log, i) => (
            <div key={i} style={{ color: log.includes('✅') ? '#00ff00' : log.includes('❌') ? '#ff6b6b' : '#00ff00' }}>
              {log}
            </div>
          ))
        )}
      </div>

      <div style={{ marginTop: '20px', padding: '15px', background: '#fef3c7', borderRadius: '8px' }}>
        <h3>📊 Interpretation:</h3>
        <ul>
          <li>
            <strong>Write succeeds:</strong> Firestore is writable
          </li>
          <li>
            <strong>Write fails with timeout:</strong> Cloudflare is blocking writes
          </li>
          <li>
            <strong>Read succeeds:</strong> Firestore is readable
          </li>
          <li>
            <strong>Read fails with timeout:</strong> Cloudflare is blocking reads
          </li>
          <li>
            <strong>Both fail:</strong> Firestore might be down, or domain not authorized
          </li>
        </ul>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', background: '#fee2e2', borderRadius: '8px' }}>
        <h3>🔍 If Writes/Reads Fail:</h3>
        <ol>
          <li>Check Cloudflare Settings:
            <ul>
              <li>Go to Caching → Rules</li>
              <li>Make sure no rules are caching/blocking `*.googleapis.com`</li>
              <li>Add rule: Bypass Cache for path `/*`</li>
            </ul>
          </li>
          <li>Check Firestore Rules:
            <ul>
              <li>Go to Firestore → Rules</li>
              <li>Make sure rules allow reads/writes (at least for testing)</li>
            </ul>
          </li>
          <li>Check if Firestore is initialized:
            <ul>
              <li>Go to Firebase Console → Firestore</li>
              <li>Make sure database is created and in `Read/Write` mode</li>
            </ul>
          </li>
        </ol>
      </div>
    </div>
  );
};
